import { Color, Spacing } from '@persona-id/design-tokens';
import { forwardRef, ReactNode } from 'react';
import { NavLink } from 'react-router-dom-v5-compat';
import styled, { css } from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Tooltip from 'design-system/components/Tooltip';
import hoverableMixin from 'design-system/mixins/hoverable';
import menuItemMixins from 'design-system/mixins/menuItem';

const editorButtonCSS = css<{
  active?: boolean;
  disabled?: boolean;
  hasOnlyIcon?: boolean;
}>`
  ${hoverableMixin};
  background-color: ${({ theme }) => theme.color.primary.white};
  width: ${({ hasOnlyIcon }) => (hasOnlyIcon ? '32px' : 'auto')};

  height: 32px;
  border-radius: ${Spacing[1]};
  padding: ${Spacing[2]};
  margin: ${Spacing[2]} 0;

  ${({ theme, active }) =>
    active
      ? `background-color: ${theme.color.primary.brand100} !important;
         color: ${theme.color.primary.brand700};`
      : `background-color: ${theme.color.primary.white} !important`}

  span {
    margin-left: ${Spacing[1]};
  }

  &:hover:enabled,
  &:hover:focus:not(:disabled),
  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    ${menuItemMixins.hover}
    color: ${({ theme }) => theme.color.primary.brand700};
  }

  &:hover:focus:disabled,
  &:hover:disabled,
  &:focus:disabled {
    background-color: ${({ theme }) => theme.color.primary.white};
    color: ${Color.CoreSlate300};
    cursor: not-allowed;
  }
`;

const StyledTooltip = styled(Tooltip)`
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
`;

const ToolButton = styled(Button).attrs(() => ({
  variant: 'inverse',
  type: 'button',
}))<{
  active: boolean;
  disabled: boolean;
  hasOnlyIcon: boolean;
}>`
  ${editorButtonCSS}
`;

const NavLinkButton = styled(NavLink).attrs(() => ({
  variant: 'inverse',
}))`
  display: flex;
  align-items: center;
  color: ${Color.PrimaryBlack};
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  font-weight: ${({ theme }) => theme.buttonFontWeight};
  ${editorButtonCSS}

  // reset for outline on nav link
  &:focus-visible {
    outline: none;
  }

  &.active {
    background-color: ${({ theme }) => theme.color.primary.brand100};
    color: ${({ theme }) => theme.color.primary.brand700};
  }
`;

const ToolButtonText = styled.span`
  margin-left: ${Spacing[1]};
`;

const noop = () => {
  return;
};

type ToolbarControlButtonProps = {
  id: string;
  className?: string;
  text?: string;
  tooltipText: ReactNode;
  icon: ReactNode;
  to?: string;
  active?: boolean;
  disabled?: boolean;
  interactive?: boolean;
  tooltipContent?: ReactNode;
  isInternalLink?: boolean;
  ariaLabel?: string;
  onClick?: () => void;
};

export const ToolbarControlButton = forwardRef<HTMLButtonElement, ToolbarControlButtonProps>(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  (
    {
      id,
      className,
      text,
      tooltipText,
      icon,
      to,
      active = false,
      disabled = false,
      tooltipContent,
      interactive = false,
      isInternalLink,
      ariaLabel,
      onClick = noop,
    }: ToolbarControlButtonProps,
    forwardedRef: (instance: HTMLButtonElement) => void,
  ) => {
    const internalLink = to != null && isInternalLink;

    const trigger = internalLink ? (
      <NavLinkButton
        id={id}
        data-test={id}
        onClick={onClick}
        role='button'
        to={to}
        aria-label={ariaLabel || 'Toolbar internal link button'}
      >
        {icon}
        {text && <ToolButtonText>{text}</ToolButtonText>}
      </NavLinkButton>
    ) : (
      <ToolButton
        id={id}
        data-test={id}
        active={active}
        disabled={disabled}
        onClick={onClick}
        hasOnlyIcon={Boolean(icon) && !text}
        role='button'
        to={to != null ? to : undefined}
        ref={forwardedRef}
        className={className}
        aria-label={ariaLabel || 'Toolbar Control Button'}
      >
        {icon}
        {text && <ToolButtonText>{text}</ToolButtonText>}
      </ToolButton>
    );

    // Note(Evan): Set the portal for tooltips to render above sideNav in cases where left-most tooltips
    // collide with sideNav
    return (
      <StyledTooltip
        side='top'
        trigger={trigger}
        interactive={interactive}
        zIndex={Z_INDICES.topNavTooltip}
      >
        {tooltipText}
        {tooltipContent}
      </StyledTooltip>
    );
  },
);
ToolbarControlButton.displayName = 'ToolbarControlButton';
